import { useEffect } from "react";
import { AlertTriangle, Ban, RotateCcw, Trash2, UserPlus, X } from "lucide-react";
import Button from "../ui/Button";

const CONFIG = {
  deactivate: {
    icon: Ban,
    title: "Deactivate institution",
    tone: "from-amber-500/90 to-orange-600/90",
    description:
      "Sets status to inactive while keeping the record. Portal login and new uploads pause until the institution is activated again.",
    confirm: "Deactivate",
    variant: "primary",
    confirmClass: "bg-amber-600 hover:bg-amber-700 shadow-amber-600/20",
    warn: true,
  },
  activate: {
    icon: UserPlus,
    title: "Activate institution",
    tone: "from-emerald-600/90 to-emerald-700/90",
    description:
      "Sets status to active and restores business portal access so collectors can work assigned cases again.",
    confirm: "Activate",
    variant: "accent",
    warn: false,
  },
  restore: {
    icon: RotateCcw,
    title: "Restore institution",
    tone: "from-emerald-600/90 to-emerald-700/90",
    description:
      "Clears deleted_at, sets status to active, and restores provider portal login for this institution.",
    confirm: "Restore & reopen login",
    variant: "accent",
    warn: false,
  },
  remove: {
    icon: Trash2,
    title: "Delete institution",
    tone: "from-red-600/90 to-red-700/90",
    description:
      "Soft-deletes this institution. It stays on the Institutions list as Deleted so you can restore it later. Portal login is blocked until restore.",
    confirm: "Delete institution",
    variant: "primary",
    confirmClass: "bg-red-600 hover:bg-red-700 shadow-red-600/20",
    warn: true,
  },
};

/** @deprecated aliases kept for callers still using old action names */
const ALIASES = {
  suspend: "deactivate",
  reactivate: "activate",
};

export default function InstitutionActionModal({ type, institution, loading, onConfirm, onClose }) {
  useEffect(() => {
    if (!type) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !loading) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [type, onClose, loading]);

  if (!type || !institution) return null;

  const action = ALIASES[type] || type;
  const config = CONFIG[action] || CONFIG.deactivate;
  const Icon = config.icon;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-primary/40 p-4 backdrop-blur-sm sm:items-center"
      onClick={() => !loading && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-[0_24px_64px_-12px_rgba(127,29,29,0.35)]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className={`bg-gradient-to-r ${config.tone} px-5 py-4 text-white`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15">
                <Icon className="h-5 w-5" />
              </span>
              <div>
                <h2 className="text-lg font-bold">{config.title}</h2>
                <p className="mt-0.5 text-sm text-white/85">{institution.name}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="rounded-lg p-1 text-white/70 hover:bg-white/10 hover:text-white disabled:opacity-50"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="px-5 py-5">
          <p className="text-sm leading-relaxed text-primary/65">{config.description}</p>

          {config.warn ? (
            <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200/70 bg-amber-50/60 px-3.5 py-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
              <p className="text-xs leading-relaxed text-amber-900/80">
                {institution.activeCases} active cases · institution portal login will be affected.
              </p>
            </div>
          ) : null}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-primary/[0.06] bg-primary/[0.02] px-5 py-4 sm:flex-row sm:justify-end">
          <Button variant="outline" size="sm" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant={config.variant}
            size="sm"
            className={config.confirmClass}
            disabled={loading}
            onClick={async () => {
              await onConfirm();
              onClose();
            }}
          >
            {loading ? "Working…" : config.confirm}
          </Button>
        </div>
      </div>
    </div>
  );
}
